import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { listAssetsByDraftSession, resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';

export function useFleaMarketOccurrenceGalleryAssets(occurrenceId: string | null | undefined) {
  return useQuery({
    queryKey: ['flea-market-occurrence-gallery-assets', occurrenceId],
    enabled: !!occurrenceId,
    queryFn: async (): Promise<MediaAsset[]> => {
      if (!occurrenceId) return [];
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('owner_type', 'flea_market_occurrence')
        .eq('owner_id', occurrenceId)
        .eq('kind', 'gallery')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as MediaAsset[];
    },
  });
}

export function useFleaMarketOccurrenceDraftGalleryAssets(draftSessionId: string | null | undefined) {
  return useQuery({
    queryKey: ['flea-market-occurrence-draft-gallery-assets', draftSessionId],
    enabled: !!draftSessionId,
    queryFn: async (): Promise<MediaAsset[]> => {
      if (!draftSessionId) return [];
      const data = await listAssetsByDraftSession(draftSessionId);
      return data.filter((asset) => asset.owner_type === 'flea_market_occurrence' && asset.kind === 'gallery');
    },
  });
}

export function useFleaMarketOccurrenceGalleryUrlMap(occurrenceIds: string[]) {
  const stableIds = occurrenceIds.length > 0 ? [...new Set(occurrenceIds)].sort() : [];

  return useQuery({
    queryKey: ['flea-market-occurrence-gallery-map', stableIds.join(',')],
    enabled: stableIds.length > 0,
    queryFn: async (): Promise<Record<string, string[]>> => {
      if (stableIds.length === 0) return {};
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('owner_type', 'flea_market_occurrence')
        .eq('kind', 'gallery')
        .in('owner_id', stableIds)
        .order('sort_order', { ascending: true });
      if (error) throw error;

      const rows = (data ?? []) as unknown as MediaAsset[];
      const map: Record<string, string[]> = {};

      rows.forEach((asset) => {
        const ownerId = asset.owner_id ?? '';
        if (!ownerId) return;
        const url = resolveAssetUrl(asset, 640);
        if (!url) return;
        if (!map[ownerId]) map[ownerId] = [];
        map[ownerId].push(url);
      });

      return map;
    },
    initialData: {} as Record<string, string[]>,
  });
}
